import { useState, useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { BACKEND_URL } from "@/constants/api";
import { PageHeader } from "@/layouts/PageHeader";
import { LucideUsers, LucideLogOut } from "lucide-react";
import ClassList from "@/components/entities/ClassList";
import { EmptyState } from "@/components/ui/EmptyState";

export default function SharedClasses() {
  const { getToken } = useAuth();

  const [sharedClasses, setSharedClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const fetchSharedClasses = async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}api/shared-classes`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error("Failed to fetch shared classes");
        setSharedClasses(await res.json());
        setFetchError(null);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Error fetching shared classes:", err);
        setFetchError("Could not load shared classes. Please refresh the page.");
      } finally {
        setLoading(false);
      }
    };
    fetchSharedClasses();
    return () => controller.abort();
  }, [getToken]);

  const handleLeave = async (item) => {
    if (
      !confirm(
        `Leave "${item.name}"? You will lose access to this shared class.`
      )
    )
      return;
    try {
      const token = await getToken();
      await fetch(`${BACKEND_URL}api/shared-classes/${item._id}/leave`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      setSharedClasses((prev) => prev.filter((c) => c._id !== item._id));
    } catch (err) {
      console.error("Error leaving shared class:", err);
    }
  };

  return (
    <div className="px-10 py-9 max-w-[960px] mx-auto max-md:px-4 max-md:pt-5 max-md:pb-6">
      <PageHeader
        title="Shared Classes"
        subtitle="Classes that other teachers have shared with you."
      />

      {fetchError && (
        <div className="mb-4 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {fetchError}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-[60px] bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      ) : sharedClasses.length === 0 ? (
        <EmptyState
          icon={LucideUsers}
          title="No shared classes yet"
          description="When another teacher shares a class with you, it will appear here."
        />
      ) : (
        <ClassList
          items={sharedClasses}
          getLinkPath={(item) => `/shared-classes/${item._id}`}
          getOwnerName={(item) => item.sharedBy?.name ? item.sharedBy.name : undefined}
          getPermission={(item) => item.permission}
          getMenuItems={(item) => [
            { label: "Leave class", icon: LucideLogOut, onClick: () => handleLeave(item), destructive: true },
          ]}
        />
      )}
    </div>
  );
}
